import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import {
	ArticleCardLink,
	ArticleImage,
	ContentErrorState,
} from "@/components/blocks/editorial";
import MarkdownContent from "@/components/blocks/MarkdownContent";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	getAgentContentUrls,
	getAgentReadableRedirectPath,
	getCanonicalPageUrl,
} from "@/lib/ai-content";
import {
	ANALYTICS_EVENTS,
	useAnalytics,
	useContentReadTracking,
} from "@/lib/analytics";
import { type DatedPost, getClosestArticlesByDate } from "@/lib/blog";
import { getBlogSeoOverride } from "@/lib/blog-seo";
import { SCHEDULE_VISIO_URL } from "@/lib/constants";
import { content } from "@/lib/content";
import { renderContentMarkdown } from "@/lib/content-markdown";
import { getTranslator } from "@/lib/i18n";
import { buildPath, resolveLocaleForPath } from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";
import { requirePublicContent } from "@/lib/public-content";
import {
	buildArticleStructuredData,
	buildBreadcrumbStructuredData,
	buildFaqStructuredData,
	buildSeo,
	type StructuredData,
	stripHtml,
	toExcerpt,
} from "@/lib/seo";

export const Route = createFileRoute("/{-$locale}/blog/$slug")({
	component: BlogDetailPage,
	loader: async ({ params, location, serverContext }) => {
		const locale = resolveLocaleForPath(location.pathname, serverContext);
		const agentReadableRedirectPath = getAgentReadableRedirectPath(
			location.pathname,
			locale,
		);
		if (agentReadableRedirectPath) {
			throw redirect({
				href: buildPath(
					agentReadableRedirectPath,
					location.searchStr,
					location.hash,
				),
			});
		}

		const [result, posts] = await Promise.all([
			content.getPostDetails({ data: { slug: params.slug, locale } }),
			content.getPosts({ data: { locale } }),
		]);

		const { content: post, availableLocales } = requirePublicContent(
			result,
			`/blog/${params.slug}`,
			location.searchStr,
			location.hash,
		);

		const contentHtml = renderContentMarkdown(post.content, post.media);

		return { post, contentHtml, posts, locale, availableLocales };
	},
	head: ({ loaderData }) => {
		if (!loaderData) return {};
		const t = getTranslator(loaderData.locale);
		const descriptionFallback = t((t) => t.blog.description);
		const contentText = stripHtml(loaderData.contentHtml);
		const seoOverride = getBlogSeoOverride(
			loaderData.post.slug,
			loaderData.locale,
		);
		const title = seoOverride?.title ?? loaderData.post.title;
		const description =
			seoOverride?.description ??
			(contentText ? toExcerpt(contentText) : descriptionFallback);
		const path = `/blog/${loaderData.post.slug}`;
		const structuredData: StructuredData[] = [
			buildArticleStructuredData({
				title,
				description,
				path,
				locale: loaderData.locale,
				imageUrl: loaderData.post.illustration?.socialSrc,
				publishedAt: loaderData.post.publishedAt,
				modifiedAt: loaderData.post.updatedAt,
			}),
			buildBreadcrumbStructuredData([
				{
					name: t((t) => t.nav.home),
					url: getCanonicalPageUrl("/", loaderData.locale),
				},
				{
					name: t((t) => t.nav.blog),
					url: getCanonicalPageUrl("/blog", loaderData.locale),
				},
				{
					name: title,
					url: getCanonicalPageUrl(path, loaderData.locale),
				},
			]),
		];

		if (seoOverride?.faq.length) {
			structuredData.push(buildFaqStructuredData(seoOverride.faq));
		}

		return buildSeo({
			availableLocales: loaderData.availableLocales,
			title,
			description,
			path,
			locale: loaderData.locale,
			imageUrl: loaderData.post.illustration?.socialSrc,
			type: "article",
			agentReadable: getAgentContentUrls(path, loaderData.locale),
			structuredData,
		});
	},
	pendingComponent: () => (
		<div className="mx-auto w-full max-w-4xl">
			<div className="flex flex-col gap-8 px-6 py-16">
				<Skeleton className="h-10 w-3/4 mx-auto" />
				<Skeleton className="w-full aspect-video" />
				<div className="space-y-4">
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
				</div>
			</div>
		</div>
	),
	errorComponent: ({ error }) => <BlogPostError error={error} />,
});

function BlogDetailPage() {
	const { post, contentHtml, posts } = Route.useLoaderData();
	const { t, locale, localeParam } = useI18n();
	const { capture } = useAnalytics();
	const localeParams: Record<string, string> = localeParam
		? { locale: localeParam }
		: {};
	const suggestedArticles = getClosestArticlesByDate(post, posts, 3);
	const seoOverride = getBlogSeoOverride(post.slug, locale);
	const displayTitle = seoOverride?.title ?? post.title;

	useContentReadTracking({
		contentType: "blog",
		slug: post.slug,
		title: displayTitle,
		locale,
	});

	return (
		<article className="mx-auto w-full max-w-4xl">
			<div className="flex flex-col gap-8 px-6 py-16">
				<Link
					to="/{-$locale}/blog"
					params={localeParams}
					className="text-kicker hover:text-secondary"
				>
					{t((t) => t.blog.back)}
				</Link>
				<h1 className="text-heading text-center text-3xl md:text-5xl">
					{displayTitle}
				</h1>
				<p className="text-kicker text-center text-sm">
					{post.publishedAtLabel}
				</p>
				<div className="image-frame w-full aspect-video">
					<span className="image-frame-overlay" aria-hidden="true" />
					<ArticleImage
						image={post.illustration}
						sizes="(min-width: 944px) 896px, calc(100vw - 48px)"
						fetchPriority="high"
						loading="eager"
						alt={displayTitle}
						className="image-treated relative h-full w-full object-cover"
					/>
				</div>
				<MarkdownContent contentHtml={contentHtml} />
				{seoOverride ? <SeoInsightSection override={seoOverride} /> : null}
				<div className="mt-16 space-y-4">
					<aside className="border border-rule-2 bg-paper-2/55 p-6">
						<p className="font-dots text-2xl font-extrabold text-ink">
							{t((t) => t.nav.brand)}
						</p>
						<p className="mt-1 font-mono text-[0.85rem] text-ink">
							{t((t) => t.home.hero.title)}
						</p>
						<p className="mt-3 leading-relaxed text-ink-2">
							{t((t) => t.home.hero.intro)}
						</p>
					</aside>
					<Button asChild>
						<a
							href={SCHEDULE_VISIO_URL}
							target="_blank"
							rel="noreferrer"
							onClick={() =>
								capture(ANALYTICS_EVENTS.ctaClick, {
									cta: "schedule_call",
									placement: "blog_post",
									href: SCHEDULE_VISIO_URL,
								})
							}
						>
							{t((t) => t.blog.bookCtaButton)}
							<ArrowUpRight className="size-4" />
						</a>
					</Button>
				</div>

				{suggestedArticles.length > 0 ? (
					<div className="mt-16 border-t border-dashed border-rule-2 pt-16">
						<h3 className="text-heading mb-8 text-center text-2xl">
							{t((t) => t.blog.other)}
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
							{suggestedArticles.map((article) => (
								<SuggestedArticleCard
									key={article.slug}
									article={article}
									localeParams={localeParams}
								/>
							))}
						</div>
					</div>
				) : null}
			</div>
		</article>
	);
}

function SeoInsightSection({
	override,
}: {
	override: NonNullable<ReturnType<typeof getBlogSeoOverride>>;
}) {
	const { locale } = useI18n();
	const labels =
		locale === "fr-FR"
			? { takeaways: "À retenir", faq: "FAQ rapide" }
			: { takeaways: "Key takeaways", faq: "Quick FAQ" };

	return (
		<Card showPin={false} className="mt-12 p-6">
			<p className="text-kicker">{labels.takeaways}</p>
			<p className="text-body-muted mt-3">{override.summary}</p>
			<ul className="text-body-muted mt-5 space-y-3 text-sm">
				{override.keyPoints.map((point) => (
					<li key={point} className="flex gap-3">
						<span className="mt-1.5 shrink-0 font-mono text-ink-3">&gt;</span>
						<span>{point}</span>
					</li>
				))}
			</ul>
			<div className="mt-8 space-y-4">
				<h2 className="text-heading text-xl">{labels.faq}</h2>
				{override.faq.map((item) => (
					<div
						key={item.question}
						className="border-t border-dashed border-rule-2 pt-4"
					>
						<h3 className="text-base font-semibold text-foreground">
							{item.question}
						</h3>
						<p className="text-body-muted mt-2 text-sm">{item.answer}</p>
					</div>
				))}
			</div>
		</Card>
	);
}

function SuggestedArticleCard({
	article,
	localeParams,
}: {
	article: DatedPost;
	localeParams: Record<string, string>;
}) {
	return (
		<ArticleCardLink
			article={article}
			localeParams={localeParams}
			placement="blog_suggested"
			variant="compact"
		/>
	);
}

function BlogPostError({ error }: { error: Error }) {
	const { t } = useI18n();

	return (
		<ContentErrorState
			error={error}
			title={t((t) => t.blog.errorTitleDetail)}
			fallback={t((t) => t.blog.errorFallback)}
			source="blog_detail"
		/>
	);
}
